import type {
  BackendEvent,
  BackendEventDetail,
  BackendLocality,
  Event,
  EventCategory,
  EventLocation,
  Locality,
} from "@/types";

// ── Date parsing ──

const SPANISH_MONTH_MAP: Record<string, number> = {
  ENERO: 1, FEBRERO: 2, MARZO: 3, ABRIL: 4, MAYO: 5, JUNIO: 6,
  JULIO: 7, AGOSTO: 8, SEPTIEMBRE: 9, OCTUBRE: 10, NOVIEMBRE: 11, DICIEMBRE: 12,
};

/**
 * Parse backend date string.
 * Accepts ISO 8601 (e.g. "2025-03-21") or Spanish date strings (e.g. "JUEVES 07 DE MAYO").
 * For Spanish dates without a year, infers the upcoming occurrence.
 */
export function parseBackendDate(dateStr: string | null): string | null {
  if (!dateStr) return null;

  // Try ISO 8601 first (fastest path)
  const isoDate = new Date(dateStr);
  if (!isNaN(isoDate.getTime())) return isoDate.toISOString();

  // Try Spanish date format: "JUEVES 07 DE MAYO", "07 DE MAYO", "MAYO 07", etc.
  const normalized = dateStr.toUpperCase().replace(/ DE /g, " ");
  const parts = normalized.split(/\s+/).filter(Boolean);

  let day: number | undefined;
  let month: number | undefined;

  for (const part of parts) {
    const asNum = parseInt(part, 10);
    if (!isNaN(asNum) && asNum >= 1 && asNum <= 31) {
      day = asNum;
    } else if (SPANISH_MONTH_MAP[part]) {
      month = SPANISH_MONTH_MAP[part];
    }
  }

  if (!day || !month) return null;

  // Infer year: use current year, bump to next year if date has already passed
  const now = new Date();
  let year = now.getFullYear();
  const candidate = new Date(year, month - 1, day);
  if (candidate < now) year += 1;

  return new Date(year, month - 1, day).toISOString();
}

// ── Slug generation ──

/** Generate a URL-safe slug from an event name. */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove accents
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, "") // Remove leading/trailing hyphens
    .slice(0, 50); // Limit length
}

// ── Category inference ──

const CATEGORY_KEYWORDS: Record<EventCategory, string[]> = {
  conciertos: [
    "concierto", "sinfonico", "sinfónico", "orquesta", "rock", "metal", "jazz",
    "banda", "musica", "música", "tour", "show", "reggaeton", "salsa", "cumbia",
    "vallenato", "medardo", "players", "acustico", "acústico", "tributo", "gira",
    "festival musical", "opera", "ópera", "bolero", "pop", "rap", "hip hop",
    "bachata", "merengue", "techno", "edm", "electronica", "electrónica",
    "dj set", "live music", "artista", "cantante", "banda sinfonica",
  ],
  deportes: [
    "futbol", "fútbol", "deporte", "campeonato", "torneo", "liga", "partido",
    "basket", "baloncesto", "tenis", "atletismo", "maraton", "maratón", "carrera",
    "ciclismo", "boxeo", "lucha", "natacion", "natación", "voleibol", "beisbol",
    "béisbol", "rugby", "golf", "squash", "pádel", "padel", "triatlón", "triatlon",
    "olimpiada", "juegos", "competencia", "competición",
  ],
  fiestas: [
    "fiesta", "party", "carnaval", "noche de", "night", "club", "discoteca",
    "baile", "dance", "after", "brunch", "pool party", "halloween", "navidad",
    "año nuevo", "new year", "gala", "cena", "aniversario", "cumpleaños",
    "boda", "celebracion", "celebración", "verbena",
  ],
  "stand-up": [
    "stand-up", "standup", "stand up", "comedia", "humor", "comediante",
    "chiste", "monologo", "monólogo", "comico", "cómico", "show de comedia",
    "noche de humor",
  ],
  otros: [],
};

/**
 * Infer event category from name and description when backend doesn't provide it.
 * Normalizes text (lowercase, no accents) then checks keyword lists in priority order.
 * Returns "otros" only when no keyword matches.
 */
function inferCategory(name: string, description: string | null): EventCategory {
  const normalize = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const text = normalize(`${name} ${description ?? ""}`);

  const priority: EventCategory[] = ["stand-up", "deportes", "fiestas", "conciertos"];
  for (const cat of priority) {
    if (CATEGORY_KEYWORDS[cat].some((kw) => text.includes(normalize(kw)))) {
      return cat;
    }
  }
  return "otros";
}

const DEFAULT_MAX_TICKETS_PER_PURCHASE = 10;

// ── Main mappers ──

/** Map a BackendEvent to a frontend Event using backend-provided enrichment data. */
export function mapBackendEventToEvent(be: BackendEvent): Event {
  const category: EventCategory =
    be.category ?? inferCategory(be.name, be.description);
  const location: EventLocation = be.location
    ? {
        country: be.location.country || "Ecuador",
        city: be.location.city || "",
        venue: be.location.venue || "",
        addressLine: be.location.addressLine,
      }
    : {
        country: "Ecuador",
        city: "",
        venue: "",
        addressLine: be.address ?? "",
      };

  return {
    id: String(be.id),
    name: be.name,
    slug: be.sku || generateSlug(be.name),
    location,
    dateevent: parseBackendDate(be.dateIso ?? be.dateevent ?? be.dateEvent),
    dateend: parseBackendDate(be.dateEnd ?? null),
    category,
    price: be.price ?? 0,
    image: be.image,
    description: be.description,
    accessibility: be.accessibility ?? null,
    duration: be.duration ?? null,
    hour: be.hour,
    ageRestriction: be.ageRestriction ?? null,
    favorite: be.id % 5 === 0,
    iva: be.tipoEvento === 2 ? 0 : 0.15,
    state: 1,
    visible: be.visible,
    maxventadigital: be.max != null && be.max > 0 ? be.max : DEFAULT_MAX_TICKETS_PER_PURCHASE,
    cedulaporboleto: 0,
    address: be.address,
    summary: be.summary ?? null,
    tipoEvento: be.tipoEvento,
    seatingType: be.seating_type ?? 'GENERAL',
    canchaImageUrl: be.cancha_image_url ?? null,
    payment_method_deuna: be.payment_method_deuna ?? null,
    payment_method_jepfast: be.payment_method_jepfast ?? null,
    payment_method_payphone: be.payment_method_payphone ?? null,
    payment_method_cooperco: be.payment_method_cooperco ?? null,
    payment_method_plux: be.payment_method_plux ?? null,
  };
}

/** Map a BackendEventDetail to a frontend Event + Localities using backend-provided enrichment data. */
export function mapBackendEventDetailToEventAndLocalities(
  bed: BackendEventDetail,
): { event: Event; localities: Locality[] } {
  const category: EventCategory =
    bed.category ?? inferCategory(bed.name, bed.description);
  const location: EventLocation = bed.location
    ? {
        country: bed.location.country || "Ecuador",
        city: bed.location.city || "",
        venue: bed.location.venue || "",
        addressLine: bed.location.addressLine,
      }
    : {
        country: "Ecuador",
        city: "",
        venue: "",
        addressLine: bed.address ?? "",
      };

  const event: Event = {
    id: String(bed.id),
    name: bed.name,
    slug: bed.sku || generateSlug(bed.name),
    location,
    dateevent: parseBackendDate(bed.dateIso ?? bed.dateevent ?? bed.dateEvent),
    dateend: parseBackendDate(bed.dateEnd ?? null),
    category,
    price: bed.price ?? 0,
    image: bed.image,
    description: bed.description,
    accessibility: bed.accessibility ?? null,
    duration: bed.duration ?? null,
    hour: bed.hour,
    ageRestriction: bed.ageRestriction ?? null,
    favorite: bed.id % 5 === 0,
    iva: bed.tipoEvento === 2 ? 0 : 0.15,
    state: bed.state,
    visible: bed.visible,
    maxventadigital: bed.max > 0 ? bed.max : DEFAULT_MAX_TICKETS_PER_PURCHASE,
    cedulaporboleto: 0,
    address: bed.address,
    summary: bed.summary ?? null,
    tipoEvento: bed.tipoEvento,
    // bug-20260930-seating-type-detalle: el detalle NO devuelve seating_type. Si acá se
    // rellenara con 'GENERAL', ese valor inventado pisaría el real del listado al mezclar
    // ambas fuentes en EventDetailPage. Se deja undefined y decide quien mezcla.
    seatingType: bed.seating_type ?? undefined,
    canchaImageUrl: bed.cancha_image_url ?? null,
    payment_method_deuna: bed.payment_method_deuna ?? null,
    payment_method_jepfast: bed.payment_method_jepfast ?? null,
    payment_method_payphone: bed.payment_method_payphone ?? null,
    payment_method_cooperco: bed.payment_method_cooperco ?? null,
    payment_method_plux: bed.payment_method_plux ?? null,
  };

  const localities = bed.localities.map((bl) =>
    mapBackendLocalityToLocality(bl, String(bed.id)),
  );

  return { event, localities };
}

/** Map a BackendLocality (from find_event) to our Locality type. */
export function mapBackendLocalityToLocality(
  bl: BackendLocality,
  eventId: string,
): Locality {
  return {
    id: String(bl.id),
    name: bl.name,
    price: bl.price,
    comision: bl.tax ?? 0,
    available: bl.available,
    max: bl.max,
    description: bl.description,
    message: null,
    state: 1,
    // localidades-mixtas RF-2/RF-4: viene del backend por localidad; default 0 (general) para datos viejos sin el campo.
    withSeats: bl.withSeats ?? 0,
    eventId,
    type: { id: "1", name: "ADULTOS" },
  };
}
